import { Component, inject, Input } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  CardModule,
  CheckboxModule,
  DropdownModule,
  RadioButtonModule,
  AlertModule,
  TooltipModule,
} from 'axis-components';
import { CommonModule, JsonPipe, SlicePipe } from '@angular/common';
import { BaseRules, TextValues } from './louisiana-model';
import { UimuiBaseStateComponent } from '../uimui-base-state.component';
import { RequestService } from '../../../_services/request/request.service';
import { ISSUING_COMPANY_COVERED_STATES } from '../../../common/constant';
import { TruncateModule } from 'axis-components/truncate';

@Component({
  selector: 'app-louisiana',
  standalone: true,
  imports: [
    CardModule,
    CheckboxModule,
    CommonModule,
    DropdownModule,
    ReactiveFormsModule,
    RadioButtonModule,
    AlertModule,
    SlicePipe,
    TruncateModule,
    TooltipModule,
  ],
  templateUrl: './louisiana.component.html',
})
export class LouisianaComponent extends UimuiBaseStateComponent<
  BaseRules,
  TextValues
> {
  requestService = inject<RequestService>(RequestService);
  policyLiabilityLimitsValue: string | undefined;

  @Input() set formGroup(form: FormGroup | null) {
    if (form) {
      super.getJsonRuleFiles('louisiana', form);
      this.addIssuingCompanyToPdfDump();
    }
  }
  @Input() set isReview(review: boolean) {
    if (review) {
      super.getJsonRuleFiles('louisiana', null);
      this.addIssuingCompanyToPdfDump();
    }
  }

  // Get Policy Liability Limits Value
  private getPolicyLiabilityLimitsValue(data: any): string | undefined {
    const requestDetails = data?.request?.requestDetails;
    let policyLiabilityLimitsValue: string | undefined;
    if (!requestDetails?.policyDetail) {
      return undefined;
    }
    if (Array.isArray(requestDetails.policyDetail)) {
      policyLiabilityLimitsValue = requestDetails.policyDetail[0].liability;
    }
    return policyLiabilityLimitsValue || undefined;
  }

  private addIssuingCompanyToPdfDump(): void {
    try {
      let issuingCompanyId;
      this.requestService.getRequestDetails().subscribe({
        next: (data: any) => {
          issuingCompanyId = this.findIssuingCompany(data);
          this.policyLiabilityLimitsValue =
            this.getPolicyLiabilityLimitsValue(data);
          const issuingCompanies = ISSUING_COMPANY_COVERED_STATES.reduce(
            (acc, company) => ({
              ...acc,
              [company.id]: company.id === issuingCompanyId,
            }),
            {},
          );

          if (!this.baseRulesService.pdfDataDump['louisiana']) {
            this.baseRulesService.pdfDataDump['louisiana'] = {};
          }

          this.baseRulesService.pdfDataDump['louisiana'] = {
            ...this.baseRulesService.pdfDataDump['louisiana'],
            ...issuingCompanies,
          };
        },
      });
    } catch (error) {
      console.error('Error adding issuing company data:', error);
    }
  }

  private findIssuingCompany(data: any): string | undefined {
    const requestDetails = data?.request?.requestDetails;
    let issuingCompanyId: string | undefined;

    if (!requestDetails?.policyDetail) {
      return undefined;
    }

    requestDetails.policyDetail.forEach((policy: any) => {
      if (policy.coveredStates?.length && policy.issuingCompany?.id) {
        const hasLouisiana = policy.coveredStates.some(
          (state: any) => state?.value === 'LA',
        );
        if (hasLouisiana) {
          issuingCompanyId = policy.issuingCompany.id;
        }
      }
    });

    return issuingCompanyId || undefined;
  }
  private clearFormControls(): void {
    const controlNames = [
      'forValue_SplitEachPerson_MinLimit_DropDown',
      'forValue_SplitEachAccident_MinLimit_DropDown',
      'forValue_CombinedSingleLimit_MinLimit_DropDown',
      'forValue_SplitEachPerson_DropDown',
      'forValue_SplitEachAccident_DropDown',
      'forValue_CombinedSingleLimit_DropDown',
    ];

    controlNames.forEach((controlName) => {
      const control = this.reactiveForm.get(controlName);
      if (control) {
        control.setValue(null);
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });
  }

  // logic for splitting and setting value from readonly and dropdown values.
  getSelectedItem(data, selectionId: string, formControlName: string) {
    this.clearFormControls();
    if (
      selectionId === 'StateMinimum_Split' ||
      selectionId === 'StateMinimum_VaryBetweenStates' ||
      selectionId === 'StateMinimum_CombinedSingleLimit'
    ) {
      if (
        formControlName === 'levelOne_Radio' ||
        formControlName === 'levelTwoUMBICoverage_Radio' ||
        formControlName === 'levelTwoEconomicOnlyUMBICoverage_Radio'
      )
        if (
          data.value == 'UMBICoverage_LevelOne_Radio' ||
          data.value == 'EconomicOnlyUMBICoverageLowerThanBI_LevelOne_Radio' ||
          data.value == 'SplitLimits_LevelTwoUMBICoverage_Radio' ||
          data.value == 'SplitLimits_LevelTwoEconomicOnlyUMBICoverage_Radio'
        ) {
          if (
            this.baseRules['forValue_Split_EpeaMin_DropDown'] &&
            this.baseRules['forValue_Split_EpeaMin_DropDown'].includes(
              'readonly',
            )
          ) {
            let mindropdown = this.controls.filter(
              (control) =>
                control.formControlName === 'forValue_Split_EpeaMin_DropDown',
            );
            if (mindropdown) {
              let minvalues = mindropdown[0]['data'][0]['name'];
              let minvalsplit = minvalues.split('/');
              this.reactiveForm
                .get('forValue_SplitEachPerson_MinLimit_DropDown')
                .setValue(minvalsplit[0]);
              this.reactiveForm
                .get('forValue_SplitEachAccident_MinLimit_DropDown')
                .setValue(minvalsplit[1]);
            }
          }
        }
      if (
        data.value == 'UMBICoverage_LevelOne_Radio' ||
        data.value == 'EconomicOnlyUMBICoverageLowerThanBI_LevelOne_Radio' ||
        data.value == 'CombinedSingleLimit_LevelTwoUMBICoverage_Radio' ||
        data.value ==
          'CombinedSingleLimit_LevelTwoEconomicOnlyUMBICoverage_Radio'
      ) {
        if (
          this.baseRules['forValue_CombinedSingleLimit_MinLimit_DropDown'] &&
          this.baseRules[
            'forValue_CombinedSingleLimit_MinLimit_DropDown'
          ].includes('readonly')
        ) {
          let mindropdown = this.controls.filter(
            (control) =>
              control.formControlName ===
              'forValue_CombinedSingleLimit_MinLimit_DropDown',
          );
          if (mindropdown) {
            let minvalues = mindropdown[0]['data'][0]['name'];
            this.reactiveForm
              .get('forValue_CombinedSingleLimit_MinLimit_DropDown')
              .setValue(minvalues);
          }
        }
      }
    }

    if (
      selectionId === 'IndividualState_Split' ||
      selectionId === 'IndividualState_VaryBetweenStates' ||
      selectionId === 'IndividualState_CombinedSingleLimit'
    ) {
      if (
        formControlName === 'forValue_Split_Epea_DropDown' ||
        formControlName === 'forValue_CombinedSingleLimit_DropDown'
      ) {
        if (
          this.baseRules['forValue_Split_Epea_DropDown'] &&
          this.baseRules['forValue_Split_Epea_DropDown'].includes('show')
        ) {
          let minvalues = data['name'];
          let minvalsplit = minvalues.split('/');
          this.reactiveForm
            .get('forValue_SplitEachPerson_DropDown')
            .setValue(minvalsplit[0]);
          this.reactiveForm
            .get('forValue_SplitEachAccident_DropDown')
            .setValue(minvalsplit[1]);
        }

        if (
          this.baseRules['forValue_CombinedSingleLimit_DropDown'] &&
          this.baseRules['forValue_CombinedSingleLimit_DropDown'].includes(
            'show',
          )
        ) {
          let minvalues = data['name'];
          this.reactiveForm
            .get('forValue_CombinedSingleLimit_DropDown')
            .setValue(minvalues);
        }
      }
    }
  }
}
